import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Form } from "react-bootstrap";
import Card from "../../../components/Card";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dropdown } from "primereact/dropdown";
import { Dialog } from "primereact/dialog";
import Loader from "../../../components/partials/components/loader";
import { getApiCall } from "../../../service/getApiCall";
import { postApiCall } from "../../../service/postApiCall";
import {
  API_MANAGE_LOCATIONS_LIST, // GET /vX/locations
  API_USERS_LIST,            // GET /vX/users
  API_BULK_LOCATION_ADD,     // <-- add in service/url.js: `${API_BASE}/locations/bulk-add`
} from "../../../service/url";
import { toast } from 'react-toastify';

export default function BulkLocationAdd() {
  const [loading, setLoading] = useState(false);

  // master lists
  const [locations, setLocations] = useState([]);   // [{location_id, location_name}]
  const [users, setUsers] = useState([]);           // [{id, username, ...}]

  // UI state
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [rows, setRows] = useState(10);
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [locRes, usersRes] = await Promise.all([
          getApiCall(API_MANAGE_LOCATIONS_LIST, {}),
          getApiCall(API_USERS_LIST, {}),
        ]);
        setLocations(Array.isArray(locRes) ? locRes : []);
        setUsers(Array.isArray(usersRes) ? usersRes : []);
      } catch (err) {
        toast.error("Failed to load locations/users");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // search within users list
  const filteredUsers = useMemo(
    () =>
      (users || []).filter((u) =>
        (u.username || "").toLowerCase().includes(searchValue.toLowerCase())
      ),
    [users, searchValue]
  );

  const clearSearch = () => {
    setSearchValue("");
  };

  const toggleUser = (userId) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  // Select-all respects current search
  const toggleAll = () => {
    const visibleIds = filteredUsers.map((u) => u.id);
    const allSelected =
      visibleIds.length > 0 && visibleIds.every((id) => selectedUserIds.includes(id));

    if (allSelected) {
      setSelectedUserIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
    } else {
      setSelectedUserIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
    }
  };

  const handleBulkAdd = async () => {
    if (!selectedLocationId) {
      toast.error("Please select a location first.");
      return;
    }
    if (selectedUserIds.length === 0) {
      toast.error("Select at least one user.");
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        user_ids: selectedUserIds,
        location_id: selectedLocationId, // <-- ensure backend expects `location_id`
      };
      const res = await postApiCall(API_BULK_LOCATION_ADD, payload);

      if (res) {
        toast.success(res.message || "Users added to location successfully");
        setSelectedUserIds([]);
        // keep selectedLocationId so you can continue adding into same location
      } else {
        toast.error(res?.message || "Failed to add users");
      }
    } catch (err) {
      toast.error(err?.message || "Error adding users to location");
    } finally {
      setSubmitting(false);
      setConfirmVisible(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div>
      <Row style={{marginTop:"20px"}}>
        <Col sm="12">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h4 className="card-title">Bulk User Location Add</h4>
            </Card.Header>

            <Card.Body className="px-0">
              {/* Controls Row */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  padding: "10px",
                  flexWrap: "wrap",
                }}
              >
                {/* Show X items */}
                <span>Show</span>
                <Dropdown
                  value={rows}
                  options={[
                    { label: "5", value: 5 },
                    { label: "10", value: 10 },
                    { label: "20", value: 20 },
                    { label: "50", value: 50 },
                  ]}
                  onChange={(e) => setRows(e.value)}
                  style={{ width: 90, height: 40, display: "flex", alignItems: "center" }}
                />
                <span>items</span>

                {/* Search */}
                <Form.Control
                  style={{ width: 250, height: 40, marginLeft: 10 }}
                  placeholder="Search"
                  type="text"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                />
                <Button label="Clear" className="p-button-secondary" onClick={clearSearch} />

                {/* Location selector */}
                <Dropdown
                  value={selectedLocationId}
                  options={(locations || []).map((l) => ({
                    label: l.location_name,
                    value: l.location_id,
                  }))}
                  onChange={(e) => setSelectedLocationId(e.value)}
                  placeholder="Select Location"
                  style={{ width: 260, marginLeft: 10 }}
                />

                {/* Submit */}
                <Button
                style={{background:"#3A57E8", border:"#3A57E8"}}
                  label="Add to Location"
                  className="p-button-success"
                  disabled={selectedUserIds.length === 0 || !selectedLocationId || submitting}
                  onClick={() => setConfirmVisible(true)}
                >
                  {submitting && (
                    <span
                      className="spinner-border spinner-border-sm ml-2"
                      role="status"
                      aria-hidden="true"
                      style={{ marginLeft: 8 }}
                    />
                  )}
                </Button>
              </div>

              {/* Table */}
              <div className="scroll-container">
                <DataTable
                  value={filteredUsers}
                  paginator
                  rows={rows}
                  tableStyle={{ minWidth: "50rem" }}
                  emptyMessage="No users found"
                >
                  <Column
                    header={
                      <Checkbox
                        checked={
                          filteredUsers.length > 0 &&
                          filteredUsers.every((u) => selectedUserIds.includes(u.id))
                        }
                        onChange={toggleAll}
                      />
                    }
                    headerStyle={{ width: "3rem" }}
                    body={(row) => (
                      <Checkbox
                        checked={selectedUserIds.includes(row.id)}
                        onChange={() => toggleUser(row.id)}
                      />
                    )}
                  />
                  <Column field="id" header="User Id" sortable />
                  <Column field="username" header="User Name" sortable />
                </DataTable>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Confirm Modal */}
      <Dialog
        header="Confirm Bulk Add"
        visible={confirmVisible}
        style={{ width: "25vw" }}
        onHide={() => setConfirmVisible(false)}
        footer={
          <div>
            <Button label="Cancel" className="p-button-text" onClick={() => setConfirmVisible(false)} />
            <Button label="Confirm" className="p-button-success" onClick={handleBulkAdd} disabled={submitting} />
          </div>
        }
      >
        <p>Are you sure you want to add {selectedUserIds.length} user(s) to this location?</p>
      </Dialog>
    </div>
  );
}
